/**
 * Preferências duráveis e prompts salvos do Chat de IA.
 *
 * As duas listas invalidam separadamente: preferência muda o que a IA lê em
 * todo turno; prompt salvo só muda os atalhos do composer. O ranking de mais
 * usados (`promptsSugeridos`) é invalidado junto com a lista de gestão — criar,
 * editar ou excluir um prompt reordena os atalhos.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarPreferenciaIa,
  atualizarPromptIa,
  criarPreferenciaIa,
  criarPromptIa,
  excluirPreferenciaIa,
  excluirPromptIa,
  fetchPreferenciasIa,
  fetchPromptsIa,
  fetchPromptsSugeridos,
  registrarUsoPromptIa,
} from '@/api/iaPersonalizacao';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

export function usePreferenciasIa(ativo = true) {
  return useQuery({
    queryKey: queryKeys.ia.preferencias,
    queryFn: fetchPreferenciasIa,
    enabled: ativo,
  });
}

export function usePromptsIa(ativo = true) {
  return useQuery({
    queryKey: queryKeys.ia.prompts,
    queryFn: fetchPromptsIa,
    enabled: ativo,
  });
}

export function usePromptsSugeridos(ativo = true) {
  return useQuery({
    queryKey: queryKeys.ia.promptsSugeridos,
    queryFn: fetchPromptsSugeridos,
    enabled: ativo,
    refetchOnWindowFocus: false,
  });
}

function useInvalidatePreferencias() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.ia.preferencias });
}

function useInvalidatePrompts() {
  const queryClient = useQueryClient();
  // `queryKeys.ia.prompts` é prefixo de `promptsSugeridos` — uma invalidação
  // cobre as duas listas.
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.ia.prompts });
}

export function useCriarPreferencia() {
  const invalidate = useInvalidatePreferencias();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: (texto: string) => criarPreferenciaIa(texto),
    onSuccess: () => {
      invalidate();
      showSuccess('Preferência salva. A IA já vai considerá-la.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useAtualizarPreferencia() {
  const invalidate = useInvalidatePreferencias();
  const { showError } = useNotification();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; texto?: string; ativo?: boolean }) =>
      atualizarPreferenciaIa(id, payload),
    onSuccess: () => invalidate(),
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useExcluirPreferencia() {
  const invalidate = useInvalidatePreferencias();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: (id: string) => excluirPreferenciaIa(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Preferência excluída.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useCriarPrompt() {
  const invalidate = useInvalidatePrompts();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: ({ titulo, texto }: { titulo: string; texto: string }) => criarPromptIa(titulo, texto),
    onSuccess: () => {
      invalidate();
      showSuccess('Prompt salvo.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useAtualizarPrompt() {
  const invalidate = useInvalidatePrompts();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: ({ id, titulo, texto }: { id: string; titulo: string; texto: string }) =>
      atualizarPromptIa(id, titulo, texto),
    onSuccess: () => {
      invalidate();
      showSuccess('Prompt atualizado.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useExcluirPrompt() {
  const invalidate = useInvalidatePrompts();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: (id: string) => excluirPromptIa(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Prompt excluído.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

/**
 * Registra o acionamento de um prompt (ranking de mais usados).
 *
 * Silencioso de propósito: é telemetria de ordenação, não uma ação do usuário.
 * Falha aqui não pode virar toast nem impedir o envio da mensagem — o texto já
 * está no composer.
 */
export function useRegistrarUsoPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => registrarUsoPromptIa(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.ia.prompts }),
    onError: () => {},
  });
}
